import React, { useEffect, useState } from "react";
import { BottomSheet } from "./BottomSheet.jsx";
import { formatAppDate } from "../utils/dateUtils.js";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const whenOf = (ts, dateFormat) => {
  const d = ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : null;
  if (!d) return "—";
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${formatAppDate(iso, dateFormat)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/**
 * Earlier versions of an edited invoice / purchase bill (newest first); each opens read-only.
 * bill: { id, editCount } · billNo: shown in the title · loadRevisions(id) → [{ editedAt, editedBy, oldGrandTotal, newGrandTotal, summary, snapshot }]
 * renderOld(snapshot, close): the read-only view of one earlier version
 */
export function BillHistorySheet({ isOpen, onClose, bill, billNo = "", loadRevisions, renderOld, dateFormat = "DD-MM-YYYY" }) {
  const [revisions, setRevisions] = useState(null);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!isOpen || !bill?.id) return;
    setRevisions(null);
    setError("");
    loadRevisions(bill.id)
      .then(setRevisions)
      .catch((err) => {
        console.error("Could not load edit history:", err);
        setError("Couldn't load the earlier versions.");
      });
  }, [isOpen, bill?.id, bill?.editCount]);

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title={`✏️ Edit history • ${billNo}`}>
        {error && <div className="form-error">{error}</div>}
        {!revisions && !error && <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1rem" }}>⏳ Loading…</div>}
        {revisions && revisions.length === 0 && <div style={{ color: "var(--text-dim)", padding: "1rem", textAlign: "center" }}>No earlier versions.</div>}
        {revisions && revisions.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            {revisions.map((r) => (
              <div key={r.id} data-testid="revision-row" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.6rem 0.75rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "center" }}>
                  <div style={{ fontSize: "0.8rem", minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{whenOf(r.editedAt, dateFormat)} • {r.editedBy}</div>
                    <div style={{ color: "var(--text-dim)" }}>{inr(r.oldGrandTotal)} → {inr(r.newGrandTotal)}</div>
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setViewing(r)}>View</button>
                </div>
                {(r.summary || []).length > 0 && (
                  <ul style={{ margin: "0.35rem 0 0 1rem", padding: 0, fontSize: "0.75rem", color: "var(--text-muted)" }}>
                    {r.summary.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </BottomSheet>

      {viewing && renderOld({ ...viewing.snapshot, id: bill.id }, () => setViewing(null))}
    </>
  );
}

export default BillHistorySheet;
